import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { COUT_UNITAIRE, GESTES_ISO_LABELS } from "@/utils/simulateurUtils";

const ALL_TRAVAUX = [
  { key: "combles", label: "Isolation combles", unite: "m²" },
  { key: "planchersBas", label: "Isolation plancher bas", unite: "m²" },
  { key: "rampant", label: "Rampant", unite: "m²" },
  { key: "murs", label: "Isolation murs intérieurs", unite: "m²" },
  { key: "fenetres", label: "Fenêtres", unite: "unité" },
  { key: "monoSplit", label: "Mono Split (1 split)", unite: "unité" },
  { key: "splitSupp", label: "Split supplémentaire", unite: "unité" },
  { key: "radiateurs", label: "Radiateurs électriques", unite: "unité" },
  { key: "vmcSimple", label: "VMC Simple flux", unite: "unité" },
  { key: "vmcDouble", label: "VMC Double flux", unite: "unité" },
  { key: "vmcDoubleCompact", label: "VMC Double flux compact", unite: "unité" },
  { key: "thermostat", label: "Kit Thermostat connecté", unite: "unité" },
  { key: "coutsFixes", label: "Coûts fixes", unite: "forfait" },
];

// Default costs from simulator
const getDefaultCouts = () => {
  const couts = {};
  ALL_TRAVAUX.forEach(t => {
    const cu = COUT_UNITAIRE[t.key];
    couts[t.key] = {
      materiel: cu?.materiel || 0,
      pose: cu?.pose || 0,
    };
  });
  return couts;
};

export default function DevisPopup174({
  open,
  onClose,
  onSave,
  nombreLogements = 1,
  existingTravaux = null,
  existingCouts = null,
  isRestricted = false,
}) {
  const isMultiLogement = nombreLogements > 1;
  const [perLogement, setPerLogement] = useState(
    existingTravaux?.perLogement || false
  );

  // Initialize travaux quantities
  const [travaux, setTravaux] = useState(() => {
    if (existingTravaux?.items) return existingTravaux.items;
    if (isMultiLogement && existingTravaux?.logements) return existingTravaux.logements;
    // Default: all zero except coutsFixes = 1
    const items = {};
    ALL_TRAVAUX.forEach(t => { items[t.key] = t.key === "coutsFixes" ? 1 : 0; });
    return items;
  });

  // Per-logement mode: array of {logementIdx, items}
  const [travauxLogements, setTravauxLogements] = useState(() => {
    if (existingTravaux?.logements) return existingTravaux.logements;
    return Array.from({ length: nombreLogements }, () => {
      const items = {};
      ALL_TRAVAUX.forEach(t => { items[t.key] = t.key === "coutsFixes" ? 1 : 0; });
      return items;
    });
  });

  // Coûts unitaires (editable)
  const [couts, setCouts] = useState(() => {
    if (existingCouts) return { ...getDefaultCouts(), ...existingCouts };
    return getDefaultCouts();
  });

  const handleTravauxChange = (key, value) => {
    setTravaux(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handleLogementTravauxChange = (logIdx, key, value) => {
    setTravauxLogements(prev => {
      const updated = [...prev];
      updated[logIdx] = { ...updated[logIdx], [key]: parseFloat(value) || 0 };
      return updated;
    });
  };

  const handleCoutChange = (key, field, value) => {
    setCouts(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: parseFloat(value) || 0 },
    }));
  };

  // Calculate total cost
  const totalCost = useMemo(() => {
    let total = 0;
    if (perLogement && isMultiLogement) {
      travauxLogements.forEach(logItems => {
        ALL_TRAVAUX.forEach(t => {
          const qty = logItems[t.key] || 0;
          const unit = (couts[t.key]?.materiel || 0) + (couts[t.key]?.pose || 0);
          total += qty * unit;
        });
      });
    } else {
      ALL_TRAVAUX.forEach(t => {
        const qty = travaux[t.key] || 0;
        const unit = (couts[t.key]?.materiel || 0) + (couts[t.key]?.pose || 0);
        total += qty * unit;
      });
    }
    return total;
  }, [travaux, travauxLogements, couts, perLogement, isMultiLogement]);

  const handleSave = () => {
    const data = {
      perLogement: perLogement && isMultiLogement,
      items: perLogement && isMultiLogement ? null : travaux,
      logements: perLogement && isMultiLogement ? travauxLogements : null,
      totalCost,
    };
    onSave(data, couts);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Détail des travaux - Devis 174
          </DialogTitle>
        </DialogHeader>

        {/* Toggle per-logement / global */}
        {isMultiLogement && (
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-sm border border-border mb-2">
            <span className="text-sm font-medium">Mode de saisie :</span>
            <Button
              variant={!perLogement ? "default" : "outline"}
              size="sm"
              onClick={() => setPerLogement(false)}
              className="rounded-sm"
            >
              Ensemble ({nombreLogements} logements)
            </Button>
            <Button
              variant={perLogement ? "default" : "outline"}
              size="sm"
              onClick={() => setPerLogement(true)}
              className="rounded-sm"
            >
              Par logement
            </Button>
          </div>
        )}

        {/* Section 1: Travaux par logement */}
        <div className="border border-border rounded-sm overflow-hidden">
          <div className="bg-muted/50 px-4 py-2 border-b border-border">
            <h3 className="font-semibold text-sm">Travaux par logement</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Travail</th>
                  {perLogement && isMultiLogement ? (
                    Array.from({ length: nombreLogements }, (_, i) => (
                      <th key={i} className="text-center px-2 py-2 font-medium text-muted-foreground">
                        Log. {i + 1}
                      </th>
                    ))
                  ) : (
                    <th className="text-center px-3 py-2 font-medium text-muted-foreground">Quantité</th>
                  )}
                  <th className="text-center px-3 py-2 font-medium text-muted-foreground">Unité</th>
                  {!isRestricted && <th className="text-right px-3 py-2 font-medium text-muted-foreground">Coût unit.</th>}
                  {!isRestricted && <th className="text-right px-3 py-2 font-medium text-muted-foreground">Total</th>}
                </tr>
              </thead>
              <tbody>
                {ALL_TRAVAUX.map(t => {
                  const unitCost = (couts[t.key]?.materiel || 0) + (couts[t.key]?.pose || 0);
                  let totalQty = 0;
                  if (perLogement && isMultiLogement) {
                    totalQty = travauxLogements.reduce((sum, log) => sum + (log[t.key] || 0), 0);
                  } else {
                    totalQty = travaux[t.key] || 0;
                  }
                  const lineCost = totalQty * unitCost;

                  return (
                    <tr key={t.key} className="border-t border-border hover:bg-muted/20">
                      <td className="px-3 py-1.5 font-medium text-xs">{t.label}</td>
                      {perLogement && isMultiLogement ? (
                        Array.from({ length: nombreLogements }, (_, i) => (
                          <td key={i} className="px-1 py-1">
                            <Input
                              type="number"
                              min="0"
                              value={travauxLogements[i]?.[t.key] || ""}
                              onChange={(e) => handleLogementTravauxChange(i, t.key, e.target.value)}
                              className="h-7 text-center text-xs w-16 mx-auto"
                              placeholder="0"
                            />
                          </td>
                        ))
                      ) : (
                        <td className="px-2 py-1">
                          <Input
                            type="number"
                            min="0"
                            value={travaux[t.key] || ""}
                            onChange={(e) => handleTravauxChange(t.key, e.target.value)}
                            className="h-7 text-center text-xs w-20 mx-auto"
                            placeholder="0"
                          />
                        </td>
                      )}
                      <td className="px-3 py-1.5 text-center text-xs text-muted-foreground">{t.unite}</td>
                      {!isRestricted && <td className="px-3 py-1.5 text-right text-xs tabular-nums">{unitCost.toLocaleString("fr-FR")} €</td>}
                      {!isRestricted && <td className="px-3 py-1.5 text-right text-xs tabular-nums font-medium">
                        {lineCost > 0 ? `${lineCost.toLocaleString("fr-FR")} €` : "—"}
                      </td>}
                    </tr>
                  );
                })}
                {!isRestricted && (
                <tr className="border-t-2 border-border bg-muted/30">
                  <td colSpan={perLogement && isMultiLogement ? nombreLogements + 2 : 3} className="px-3 py-2 font-semibold text-sm">
                    Total travaux
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-sm tabular-nums" colSpan="2">
                    {totalCost.toLocaleString("fr-FR")} €
                  </td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 2: Coûts unitaires — Renova only */}
        {!isRestricted && (
        <div className="border border-border rounded-sm overflow-hidden mt-4">
          <div className="bg-muted/50 px-4 py-2 border-b border-border">
            <h3 className="font-semibold text-sm">Coûts unitaires (modifiables en cas de besoin)</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30">
                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Travail</th>
                <th className="text-center px-3 py-2 font-medium text-muted-foreground">Matériel (€)</th>
                <th className="text-center px-3 py-2 font-medium text-muted-foreground">Pose (€)</th>
                <th className="text-right px-3 py-2 font-medium text-muted-foreground">Total (€)</th>
              </tr>
            </thead>
            <tbody>
              {ALL_TRAVAUX.map(t => (
                <tr key={t.key} className="border-t border-border hover:bg-muted/20">
                  <td className="px-3 py-1.5 font-medium text-xs">{t.label}</td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      min="0"
                      value={couts[t.key]?.materiel ?? ""}
                      onChange={(e) => handleCoutChange(t.key, "materiel", e.target.value)}
                      className="h-7 text-center text-xs w-20 mx-auto"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <Input
                      type="number"
                      min="0"
                      value={couts[t.key]?.pose ?? ""}
                      onChange={(e) => handleCoutChange(t.key, "pose", e.target.value)}
                      className="h-7 text-center text-xs w-20 mx-auto"
                    />
                  </td>
                  <td className="px-3 py-1.5 text-right text-xs tabular-nums font-medium">
                    {((couts[t.key]?.materiel || 0) + (couts[t.key]?.pose || 0)).toLocaleString("fr-FR")} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        <DialogFooter className="mt-4">
          <div className="flex items-center justify-between w-full">
            {!isRestricted && (
            <div className="text-sm">
              <span className="text-muted-foreground">Coût total travaux : </span>
              <span className="font-bold text-lg tabular-nums">{totalCost.toLocaleString("fr-FR")} €</span>
            </div>
            )}
            {isRestricted && <div />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="rounded-sm">
                Annuler
              </Button>
              <Button onClick={handleSave} className="rounded-sm">
                <Save className="w-4 h-4 mr-2" />
                Enregistrer les travaux
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
